<template>
  <section class="relative z-10 -mb-24 w-full md:-mb-50">
    <div class="pointer-events-none absolute inset-x-0 top-0 z-0 flex justify-center overflow-hidden">
      <PreFooterArch />
    </div>

    <!-- Starts at the arch's deepest point (152.177 of its 2698-wide viewBox, so 8.46vw below md,
         152px fixed from md) so the card's own gradient only fills in below the curve — the dip
         itself reveals the page background behind the section instead of the same fill colour. -->
    <div class="absolute inset-x-0 top-[8.46vw] bottom-0 z-0 bg-[linear-gradient(180deg,var(--color-head-office-40)_-11.8%,var(--color-head-office-20)_110.73%)] md:top-[152px]" />

    <Container class="relative z-10 pb-12 pt-20 md:pb-30 md:pt-80">
      <Grid class="items-center gap-y-8 lg:gap-y-0 lg:gap-x-8!">
        <div class="col-span-12 flex flex-col items-start gap-4 lg:col-span-3">
          <Logo variant="footer-dark" />
          <Typography
            v-if="payoff"
            as="p"
            variant="header-3"
            class="max-w-sm">
            {{ payoff }}
          </Typography>
          <Link
            v-if="link"
            variant="body"
            underline
            has-icon
            as="NuxtLink"
            :to="link.url"
            :target="link.openInNewTab ? '_blank' : undefined">
            {{ link.title }}
            <template #icon>
              <ChevronRightIcon class="w-2.5 h-2.5" />
            </template>
          </Link>
        </div>

        <div class="col-span-12 lg:col-span-9">
          <div
            class="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 scroll-pl-6 lg:mx-0 lg:grid lg:grid-cols-3 lg:gap-10 lg:overflow-visible lg:px-0">
            <div v-for="card in cards" :key="card.id" class="w-[80%] shrink-0 snap-start lg:w-auto">
              <GrowArticleCard v-bind="card" class="lg:min-h-[440px]" />
            </div>
          </div>
        </div>
      </Grid>
    </Container>
  </section>
</template>

<script setup lang="ts">
import type { GrowArticleCardApiModel, LinkApiModel } from '@gro-up/api/corporate'
import { ChevronRightIcon } from '@gro-up/ui/icons'
import { Container, Grid, Typography, Link, Logo, GrowArticleCard } from '@gro-up/ui/primitives'
import { computed } from 'vue'

import PreFooterArch from './PreFooterArch.vue'

// Relaxed vs. the generated `PreFooterApiModel` (`payoff: string`): the backend sends `null` for pages
// where the editor hasn't filled in a page-specific text yet, even though the block is otherwise enabled.
type PreFooterProps = {
  payoff?: string | null
  link?: LinkApiModel
  articles: GrowArticleCardApiModel[]
}

const props = defineProps<PreFooterProps>()

// Per design, PreFooter cards always use the neutral `default` theme (gro-up zand - 50 / head-office-50 on
// hover), regardless of the article's domain `backgroundColor` (unlike GrowArticleSection.vue, which maps it).
const mapCard = (card: GrowArticleCardApiModel) => ({
  id: card.id,
  title: card.title,
  shortDescription: card.shortDescription ?? undefined,
  image: card.image ?? undefined,
  link: card.link ?? undefined,
  tags: card.tags ?? undefined,
  theme: 'default',
})

const cards = computed(() => props.articles.map(mapCard))
</script>
